import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { SegmentedControl } from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import {
  INDUSTRY_OPTIONS, CUSTOMER_SOURCES, CUSTOMER_LEAD_TYPES, EVENT_SOURCES,
} from './constants';
import { SelectField, MultiSelectField, InputField } from './form-controls';
import { useProductGroupOptions } from '@/api/product-groups';
import type { CustomerWizardValues } from './wizard-form';
import { NumericInput } from '@/components/numeric-input';

/**
 * Step 2 — business classification. Company vs. individual toggle; company mode
 * reveals the Company ID input (Phase-1 numeric stand-in for the async company
 * search + create-company modal). Industry, source, interested services (min 1)
 * and sales owner are required; event name shows only for event-type sources.
 */
export function StepBusinessInfo() {
  const { control, formState } = useFormContext<CustomerWizardValues>();
  const type = useWatch({ control, name: 'customer.customer_type' });
  const source = useWatch({ control, name: 'customer.customer_source' });
  const { data: serviceInterestOptions = [] } = useProductGroupOptions();
  const isCompany = type === 'company';
  const showEvent = EVENT_SOURCES.has(source);
  const companyError = formState.errors.company?.company_id?.message;

  return (
    <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
      <Controller
        control={control}
        name="customer.customer_type"
        render={({ field }) => (
          <Field label="Customer type">
            {() => (
              <SegmentedControl
                aria-label="Customer type"
                value={field.value}
                onValueChange={field.onChange}
                items={[
                  { value: 'company', label: 'Company' },
                  { value: 'individual', label: 'Individual' },
                ]}
              />
            )}
          </Field>
        )}
      />

      <div
        style={{
          display: 'grid',
          gap: 'var(--vn-space-4)',
          gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
        }}
      >
        {isCompany ? (
          <Controller
            control={control}
            name="company.company_id"
            render={({ field }) => (
              <Field
                label="Company ID"
                required
                error={companyError && Number(field.value) > 0 ? undefined : companyError}
                description="Enter the company record ID. Async search + create is coming soon."
              >
                {(p) => (
                  <NumericInput thousandSeparator={false} precision={0}
                    {...p}
                    min={1}
                    invalid={!!companyError && !(Number(field.value) > 0)}
                    value={field.value || ''}
                    onChange={(e) => field.onChange(Number(e.target.value))}
                    onBlur={field.onBlur}
                    placeholder="e.g. 1024"
                  />
                )}
              </Field>
            )}
          />
        ) : null}

        <SelectField
          name="customer.customer_industry"
          label="Industry"
          options={INDUSTRY_OPTIONS}
          placeholder="Select an industry"
          required
        />

        <SelectField
          name="customer.customer_source"
          label="Source"
          options={CUSTOMER_SOURCES}
          placeholder="Select a source"
          required
        />

        {showEvent ? (
          <InputField
            name="customer.customer_source_event"
            label="Event name"
            placeholder="e.g. Cloud Expo 2026"
          />
        ) : null}

        <InputField
          name="customer.customer_website"
          label="Website"
          placeholder="https://company.com"
        />

        <SelectField
          name="customer.customer_lead_type"
          label="Lead type"
          options={CUSTOMER_LEAD_TYPES}
        />
      </div>

      <MultiSelectField
        name="customer.customer_service_interestings"
        label="Interested services"
        placeholder="Select interested services"
        options={serviceInterestOptions}
        required
      />

      <Controller
        control={control}
        name="customer.customer_sale_followers"
        render={({ field }) => {
          const first = (field.value as number[])?.[0];
          const err = formState.errors.customer?.customer_sale_followers?.message;
          return (
            <Field
              label="Sales owner (employee ID)"
              required
              error={err as string | undefined}
              description="Defaults to you. Async employee search is coming soon."
            >
              {(p) => (
                <NumericInput thousandSeparator={false} precision={0}
                  {...p}
                  min={1}
                  invalid={!!err}
                  value={first || ''}
                  onChange={(e) => {
                    const n = Number(e.target.value);
                    field.onChange(n > 0 ? [n] : []);
                  }}
                  onBlur={field.onBlur}
                  placeholder="e.g. 42"
                />
              )}
            </Field>
          );
        }}
      />
    </div>
  );
}
